/**
 * Plyx mobile bottom navigation — TikTok-style 5-tab dock.
 *
 *   Home (feed) · Discover (explore) · Upload (gradient burst) ·
 *   Inbox (DMs)  · Profile (your gallery)
 *
 * Always pinned to the bottom on mobile (`fixed`), hidden on desktop
 * (`sm:hidden`) where the regular `PlyxHeader` already covers
 * navigation. Stays out of the way of safe-areas via
 * `padding-bottom: env(safe-area-inset-bottom)` so the iPhone home
 * indicator doesn't sit on top of the icons.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, ShoppingBag, Camera, MessageCircle, User } from "lucide-react";
import { usePlyxAuth } from "@/context/PlyxAuthContext";

// iter-119ss: Replaced the Discover tab with Shop (routes to the
// AfriMarket marketplace at /browse). Per product decision the user
// asked for a one-tap path from the video feed straight into the
// marketplace so viewers can buy/sell products they see in clips.
// Discover is still reachable from the header search on desktop and
// via the channel chips on the feed itself, so removing it from the
// dock is a net win.
const TABS = [
  { to: "/plyx",         icon: Home,           label: "Home",     match: (p) => p === "/plyx" || p.startsWith("/plyx/feed") },
  { to: "/browse",       icon: ShoppingBag,    label: "Shop",     match: (p) => p.startsWith("/browse") || p.startsWith("/marketplace") || p.startsWith("/product") || p.startsWith("/listing") },
  // iter-119r → 119s: centre tab routes to /plyx/upload (not /plyx/cam)
  // so users land on the page with BOTH "Record video" and "Take photo"
  // buttons. /plyx/cam was video-only and broke photo capture.
  { to: "/plyx/upload",  icon: Camera,         label: null,       match: (p) => p.startsWith("/plyx/cam") || p.startsWith("/plyx/upload"), accent: true },
  { to: "/plyx/dm",      icon: MessageCircle,  label: "Inbox",    match: (p) => p.startsWith("/plyx/dm") },
  // The profile tab uses the user handle when signed in; falls back to
  // login for anonymous visitors so the dock always works.
  { to: null,            icon: User,           label: "Me",       match: (p) => p.startsWith("/plyx/u/") || p.startsWith("/plyx/login") },
];

export default function PlyxBottomNav() {
  const { user } = usePlyxAuth();
  const { pathname } = useLocation();
  // Hide on routes where the bottom dock would interfere — live broadcast,
  // immersive recorders, full-screen upload editor, etc. Add slugs here
  // when a new full-screen surface ships.
  const HIDDEN_PREFIXES = ["/plyx/live/", "/plyx/admin", "/plyx/cam", "/plyx/studio", "/plyx/embed"];
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-[55] sm:hidden bg-[#0A0A0A]/95 backdrop-blur border-t border-white/8"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="plyx-bottom-nav"
    >
      <div className="flex items-stretch justify-around h-14">
        {TABS.map(({ to, icon: Icon, label, match, accent }, i) => {
          // Compute the actual link destination — profile + null `to`
          // resolve dynamically based on auth state.
          const href =
            to ?? (user ? `/plyx/u/${user.handle}` : "/plyx/login?next=/plyx/u/me");
          const active = match(pathname);
          if (accent) {
            return (
              <Link
                key={i}
                to={href}
                aria-label={label || "Open camera"}
                data-testid="plyx-bottomnav-camera"
                className="flex items-center justify-center -mt-2"
              >
                <span
                  className="w-12 h-9 rounded-xl flex items-center justify-center shadow-lg shadow-[#FF2D7A]/30"
                  style={{
                    background:
                      "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)",
                  }}
                >
                  <Icon className="w-5 h-5 text-black" strokeWidth={2.4} />
                </span>
              </Link>
            );
          }
          return (
            <Link
              key={i}
              to={href}
              aria-label={label}
              data-testid={`plyx-bottomnav-${(label || "tab").toLowerCase()}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${
                active ? "text-white" : "text-white/55 hover:text-white/85"
              } transition-colors`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.3 : 1.8} />
              <span className="text-[10px] font-medium tracking-tight">{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
